import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { GameServices } from '../../services/game-services';
import { CellViewModel } from './cell-view-model';
import { DragActionType, DragData } from '../shape/shape-view-model';
import { CellView } from './cell-view';
import { Subject } from 'rxjs';
import { BlocksStylesType } from '../../model/blocks-types';

export class GridViewModel {
  public get blocksLogicActionBus$() {
    return this.gameServices.gameRunnerService.blocksLogicActionBus$;
  }
  public get onGameSetupStartTrigger() {
    return this.gameServices.gameRunnerService.onGameSetupStartTrigger;
  }
  public get onGameSetupCompleteTrigger() {
    return this.gameServices.gameRunnerService.onGameSetupCompleteTrigger;
  }
  public get defaultFontFamily() {
    return this.gameServices.gameThemeService.defaultFontFamily;
  }
  public get pointsLabelFill() {
    return this.gameServices.gameThemeService.getHexColor(BlocksStylesType.PointsLabelFill);
  }
  public get pointsLabelStroke() {
    return this.gameServices.gameThemeService.getHexColor(BlocksStylesType.PointsLabelStroke);
  }
  public get onResetGameScene$() {
    return this.onResetGameScene;
  }
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get allCellViewModels() {
    return this.cellViewModels;
  }

  private cellViewModels: CellViewModel[] = [];
  private readonly onResetGameScene = new Subject<void>();

  constructor(private gameServices: GameServices) {}

  public calculateScaleFactor(gameSizeMetrics: GameSizeMetrics): {
    scaledOffsetX: number;
    scaleFactor: number;
  } {
    return this.gameServices.gameMetricsService.calculateBoardScaling(gameSizeMetrics);
  }

  public refreshShapeProjections() {
    const lastMoveProjections =
      this.gameServices.gameRunnerService.currentGame?.cellsToProjectIfLastMove() ?? [];
    this.projectCells(lastMoveProjections.map((cell) => cell.id));
  }

  public spawnGrid(): boolean {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return false;
    }

    this.cellViewModels = game.currentGrid.cells.map((cell) => {
      return new CellViewModel(this.gameServices, cell);
    });

    return true;
  }

  public handleShapeDrag(dragData: DragData, cellViews: CellView[]) {
    const matchingCells = dragData.blockPositions
      .map((blockPosition) => {
        const originX = blockPosition.x + dragData.dragX;
        const originY = blockPosition.y + dragData.dragY;
        return cellViews.find((cellView) => {
          if (!cellView.canDrop) {
            return false;
          }
          const rect = cellView.getBounds();
          return rect.contains(originX, originY);
        });
      })
      .filter((cellView): cellView is NonNullable<typeof cellView> => cellView !== undefined);

    const foundMatch = matchingCells.length === dragData.blockPositions.length;

    if (foundMatch) {
      const row = Math.min(...matchingCells.map((cell) => cell.row));
      const column = Math.min(...matchingCells.map((cell) => cell.column));
      const droppedCellId = `${row}${column}`;

      if (dragData.type === DragActionType.OnDrag) {
        const [highlightedCells, associatedBlocks] =
          this.gameServices.gameRunnerService.currentGame?.cellsToHighlight(
            dragData.shapeViewModel.currentShape,
            droppedCellId,
          ) ?? [[], []];

        this.highlightCellBlocks(highlightedCells.map((cellView) => cellView.id));
        this.highlightCells(matchingCells.map((cellView) => cellView.id));
        dragData.shapeViewModel.highlightBlocks(associatedBlocks.map((block) => block.id));
        return;
      }

      if (dragData.type === DragActionType.OnEnd) {
        //console.log(`drop cellId: ${droppedCellId}`);

        // Drop shape
        this.gameServices.gameRunnerService.currentGame?.placeShapeOnGrid(
          dragData.shapeViewModel.currentShape,
          droppedCellId,
          false,
          true,
        );
      }
    }

    // Clear highlighting
    this.highlightCellBlocks([]);
    this.highlightCells([]);
    dragData.shapeViewModel.highlightBlocks([]);
  }

  public resetGameScene() {
    this.onResetGameScene.next();
    this.cellViewModels.length = 0;
  }

  private highlightCellBlocks(ids: string[]) {
    this.cellViewModels.forEach((cellViewModel) => {
      cellViewModel.highlightCellBlocks(ids.includes(cellViewModel.id));
    });
  }

  private highlightCells(ids: string[]) {
    this.cellViewModels.forEach((cellViewModel) => {
      cellViewModel.highlightCell(ids.includes(cellViewModel.id));
    });
  }

  private projectCells(ids: string[]) {
    this.cellViewModels.forEach((cellViewModel) => {
      cellViewModel.projectCell(ids.includes(cellViewModel.id));
    });
  }
}
